/**
 * Traktor collection.nml parser.
 *
 * Native Instruments' Traktor Pro stores its library as an .nml XML file at:
 *   ~/Documents/Native Instruments/Traktor <version>/collection.nml
 *
 * The XML shape (simplified — Traktor Pro 2.x → 3.x are backwards-compat):
 *   <NML VERSION="19">
 *     <COLLECTION ENTRIES="2345">
 *       <ENTRY TITLE="Losing It" ARTIST="Fisher">
 *         <LOCATION DIR="/:Users/:dj/:Music/:Fisher/:" FILE="Losing It.mp3" VOLUME="Macintosh HD"/>
 *         <INFO GENRE="Tech House" PLAYTIME="245" PLAYTIME_FLOAT="245.023"/>
 *         <TEMPO BPM="127.500000" BPM_QUALITY="100"/>
 *         <MUSICAL_KEY VALUE="6"/>          <!-- 0..23 → Camelot -->
 *         <CUE_V2 NAME="Intro" TYPE="0" START="253.487"  HOTCUE="0"/>   <!-- ms -->
 *         <CUE_V2 NAME="Drop"  TYPE="0" START="61500.00" HOTCUE="1"/>
 *       </ENTRY>
 *     </COLLECTION>
 *     <PLAYLISTS>
 *       <NODE TYPE="FOLDER" NAME="$ROOT">
 *         <SUBNODES COUNT="1">
 *           <NODE TYPE="PLAYLIST" NAME="Peak Time">
 *             <PLAYLIST ENTRIES="12" TYPE="LIST">
 *               <ENTRY><PRIMARYKEY TYPE="TRACK" KEY="Macintosh HD/:Users/:dj/:Music/:Fisher/:Losing It.mp3"/></ENTRY>
 *             </PLAYLIST>
 *           </NODE>
 *         </SUBNODES>
 *       </NODE>
 *     </PLAYLISTS>
 *   </NML>
 *
 * We flatten to the same shape as the Serato / Rekordbox parsers so the
 * same localStorage store + UI work for all three.
 *
 * Gotchas:
 *   - CUE_V2 START is in **milliseconds** (Rekordbox is in seconds). ÷1000.
 *   - HOTCUE ≥ 0 means it's a hot cue (0..7); HOTCUE="-1" is a memory cue.
 *   - LOCATION uses `/:` as path separator. Path = `/` + DIR + FILE, with
 *     each `/:` collapsed to `/` and a leading `/:` dropped.
 *   - MUSICAL_KEY VALUE 0..23 maps to a Camelot wheel position; we keep the
 *     raw value if the mapping is unknown.
 */

// Traktor MUSICAL_KEY.VALUE (0..23) → Camelot code. Ordering per
// https://github.com/TraktorNML/traktor-nml/blob/main/musical-key.md
const CAMELOT_BY_TRAKTOR_KEY = [
  "8B","3B","10B","5B","12B","7B","2B","9B","4B","11B","6B","1B",   // 0..11 = major
  "5A","12A","7A","2A","9A","4A","11A","6A","1A","8A","3A","10A",   // 12..23 = minor
];

const traktorLocationToPath = (loc) => {
  if (!loc) return null;
  const dir = (loc.getAttribute("DIR") || "").replace(/\/:/g, "/").replace(/^\//, "");
  const file = loc.getAttribute("FILE") || "";
  // Volume is dropped — Traktor identifies drives by name (e.g. "Macintosh HD")
  // which won't match anything on the DJ's current OS. Users match by filename
  // via the "Match audio" flow anyway.
  return "/" + dir + file;
};

const basename = (path) => (path || "").split(/[/\\]/).pop() || "";

const primaryKeyToBasename = (key) => {
  if (!key) return "";
  // e.g. "Macintosh HD/:Users/:dj/:Music/:Fisher/:Losing It.mp3"
  const flat = key.replace(/\/:/g, "/");
  return basename(flat);
};

/**
 * Parse a Traktor collection.nml text blob.
 * @param {string} text
 * @returns {{ crates: Array<{ name: string, tracks: Array }> }}
 *   If PLAYLISTS is present each leaf becomes a crate; otherwise the whole
 *   collection returns as one "Traktor Collection" crate.
 */
export function parseTraktorNML(text) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Malformed XML — this doesn't look like a Traktor collection.nml.");
  }
  if (!doc.querySelector("NML")) {
    throw new Error("Missing <NML> root — this isn't a Traktor export.");
  }

  // First pass: build filename → track lookup from COLLECTION.
  const collectionByFilename = new Map();
  const allTracks = [];
  for (const el of doc.querySelectorAll("COLLECTION > ENTRY")) {
    const loc = el.querySelector("LOCATION");
    const filePath = traktorLocationToPath(loc);
    const filename = loc?.getAttribute("FILE") || basename(filePath);

    const info = el.querySelector("INFO");
    const tempo = el.querySelector("TEMPO");
    const keyEl = el.querySelector("MUSICAL_KEY");
    const keyVal = keyEl ? parseInt(keyEl.getAttribute("VALUE") ?? "-1", 10) : -1;

    const t = {
      artist: el.getAttribute("ARTIST") || "",
      title:  el.getAttribute("TITLE") || (filename || "Untitled").replace(/\.[^.]+$/, ""),
      duration_seconds: parseFloat(info?.getAttribute("PLAYTIME_FLOAT") || info?.getAttribute("PLAYTIME") || "0") || null,
      bpm: parseFloat(tempo?.getAttribute("BPM") || "0") || null,
      key: (keyVal >= 0 && keyVal < CAMELOT_BY_TRAKTOR_KEY.length) ? CAMELOT_BY_TRAKTOR_KEY[keyVal] : null,
      genre: info?.getAttribute("GENRE") || null,
      file_path: filePath,
      filename,
      // Hot cues — HOTCUE >= 0 means "hot cue N", -1 is a memory cue.
      // Traktor STORES START in ms; we normalise to seconds so hot_cues
      // stay comparable with Serato/Rekordbox.
      hot_cues: Array.from(el.querySelectorAll("CUE_V2"))
        .filter(cue => parseInt(cue.getAttribute("HOTCUE") ?? "-1", 10) >= 0)
        .slice(0, 8)
        .map(cue => ({
          name: cue.getAttribute("NAME") || "",
          start_seconds: (parseFloat(cue.getAttribute("START")) || 0) / 1000,
          num: parseInt(cue.getAttribute("HOTCUE") ?? "0", 10),
        }))
        .sort((a, b) => a.num - b.num),
    };
    if (filename) collectionByFilename.set(filename.toLowerCase(), t);
    allTracks.push(t);
  }

  // Walk PLAYLISTS tree. Leaves are NODE[TYPE="PLAYLIST"] containing a
  // <PLAYLIST TYPE="LIST"> whose child <ENTRY><PRIMARYKEY> references a
  // COLLECTION track by "Volume/:...:File.mp3".
  const crates = [];
  const walk = (node, ancestry = []) => {
    const type = node.getAttribute("TYPE");
    const name = node.getAttribute("NAME") || "";
    if (type === "PLAYLIST") {
      const listEl = node.querySelector(":scope > PLAYLIST");
      if (!listEl) return;
      const tracks = [];
      for (const entry of listEl.querySelectorAll(":scope > ENTRY > PRIMARYKEY")) {
        const key = entry.getAttribute("KEY");
        const fname = primaryKeyToBasename(key);
        const t = collectionByFilename.get(fname.toLowerCase());
        if (t) tracks.push(t);
      }
      if (tracks.length) {
        const fullName = ancestry.filter(a => a && a !== "$ROOT").concat(name).join(" / ") || name;
        crates.push({ name: fullName, tracks });
      }
    } else if (type === "FOLDER") {
      const sub = node.querySelector(":scope > SUBNODES");
      if (!sub) return;
      for (const child of sub.querySelectorAll(":scope > NODE")) {
        walk(child, ancestry.concat(name));
      }
    }
  };
  const root = doc.querySelector("PLAYLISTS > NODE");
  if (root) walk(root);

  // Fallback: no usable playlists → surface the whole collection.
  if (!crates.length && allTracks.length) {
    crates.push({ name: "Traktor Collection", tracks: allTracks });
  }

  return { crates };
}
